<template>
  <div class="audit-trail">
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 3">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Action</label>
          <FormDropdown v-model="filters.action" :options="actionOptions" placeholder="All actions" clearable />
        </div>
        <div class="rp-filter">
          <label>Entity</label>
          <FormDropdown v-model="filters.entity" :options="entityOptions" placeholder="All entities" clearable />
        </div>
        <div class="rp-filter">
          <label>User</label>
          <FormDropdown v-model="filters.actor" :options="actorOptions" placeholder="All users" searchable clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Search</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input v-model="filters.search" type="text" class="form-control" placeholder="Voucher, user, summary or type" />
          </div>
        </div>
      </div>

      <ReportActions :loading="loading" :exporting="exporting" :can-reset="hasActiveFilters" :can-print="!!rows.length"
                     @reset="resetFilters" @export="exportFile" @print="printReport" />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-navy">
        <span class="rp-card-icon"><i class="ti ti-history"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Events</div>
          <div class="rp-card-value">{{ formatInt(summary.event_count) }}</div>
          <div class="rp-card-sub">{{ plural(summary.actor_count, 'user') }} in the period</div>
        </div>
      </div>
      <div class="rp-card tone-green">
        <span class="rp-card-icon"><i class="ti ti-plus"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Creates</div>
          <div class="rp-card-value">{{ formatInt(summary.create_count) }}</div>
          <div class="rp-card-sub">new postings &amp; records</div>
        </div>
      </div>
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-pencil"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Updates</div>
          <div class="rp-card-value">{{ formatInt(summary.update_count) }}</div>
          <div class="rp-card-sub">status &amp; field changes</div>
        </div>
      </div>
      <div class="rp-card tone-teal">
        <span class="rp-card-icon"><i class="ti ti-users"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Users</div>
          <div class="rp-card-value">{{ formatInt(summary.actor_count) }}</div>
          <div class="rp-card-sub">who posted in the period</div>
        </div>
      </div>
    </div>

    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="rp-pills">
          <button type="button" class="rp-pill" :class="{ active: !filters.action }" @click="filters.action = null">
            All <span class="rp-pill-count">{{ formatInt(summary.event_count) }}</span>
          </button>
          <button v-for="entry in summary.action_counts" :key="entry.key" type="button" class="rp-pill"
                  :class="{ active: filters.action === entry.key }" @click="filters.action = entry.key">
            {{ entry.label }} <span class="rp-pill-count">{{ formatInt(entry.count) }}</span>
          </button>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          lines per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th>Date / Time</th>
              <th>User</th>
              <th>Action</th>
              <th>Type</th>
              <th>Voucher</th>
              <th>Store</th>
              <th>Summary</th>
              <th class="text-center">Details</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in pagedRows" :key="row.key">
              <td class="text-nowrap">
                <div class="fw-medium">{{ formatDate(row.date) }}</div>
                <small class="text-muted">{{ row.time || '—' }}</small>
              </td>
              <td>{{ row.actor_name || '—' }}</td>
              <td>
                <span class="rp-badge" :class="actionTone(row.action)">{{ row.action_label }}</span>
              </td>
              <td>
                <span class="rp-badge" :class="typeTone(row.type_key)">{{ row.type }}</span>
                <div class="small text-muted mt-1">{{ row.entity_label }}</div>
              </td>
              <td class="text-nowrap fw-medium">{{ row.voucher_id || '—' }}</td>
              <td>{{ row.store || '—' }}</td>
              <td><span class="rp-narration" :title="row.summary">{{ row.summary || '—' }}</span></td>
              <td class="text-center text-nowrap">
                <button type="button" class="btn btn-sm btn-outline-primary audit-detail-btn" @click="openDetails(row)">
                  <i class="ti ti-list-details me-1"></i>Details
                </button>
              </td>
            </tr>
            <tr v-if="!loading && !pagedRows.length">
              <td colspan="8">
                <div class="rp-empty">
                  <i class="ti ti-history"></i>
                  <div class="fw-semibold">No audit events found</div>
                  <small class="text-muted">Try another period, action, entity or search.</small>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <ReportPagination v-if="totalRows" :page="page" :page-count="pageCount" :pages="pageNumbers"
                        :start="pageStart" :end="pageEnd" :total="totalRows" @go="goToPage" />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Chronological trail of {{ options.store || 'the store' }}: ledger vouchers, POS sales, store transfers
      and stock batches. Click Details on a row to see every posting line or field change.
    </p>

    <!-- Details modal -->
    <div v-if="detailRow" class="audit-detail-backdrop" @click.self="closeDetails">
      <div class="audit-detail-modal" role="dialog" aria-modal="true" aria-labelledby="audit-detail-title">
        <div class="audit-detail-head">
          <div class="min-w-0">
            <h5 id="audit-detail-title" class="mb-1">Audit details</h5>
            <div class="small text-muted text-truncate">
              {{ detailRow.voucher_id || '—' }} · {{ detailRow.type }} · {{ detailRow.action_label }}
            </div>
          </div>
          <button type="button" class="btn btn-sm btn-light" aria-label="Close" @click="closeDetails">
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="audit-detail-meta">
          <div><span class="text-muted">Date</span><div class="fw-medium">{{ formatDate(detailRow.date) }} {{ detailRow.time }}</div></div>
          <div><span class="text-muted">User</span><div class="fw-medium">{{ detailRow.actor_name || '—' }}</div></div>
          <div><span class="text-muted">Store</span><div class="fw-medium">{{ detailRow.store || '—' }}</div></div>
          <div><span class="text-muted">Entity</span><div class="fw-medium">{{ detailRow.entity_label }}</div></div>
        </div>

        <p class="audit-detail-summary mb-3">{{ detailRow.summary || '—' }}</p>

        <div v-if="invoiceTypeLine" class="audit-invoice-type mb-3">
          <span class="text-muted">Invoice type</span>
          <div class="fw-semibold">{{ invoiceTypeLine }}</div>
        </div>

        <!-- 80mm sale invoice: items + payment totals -->
        <div v-if="invoiceItems.length" class="audit-receipt mb-3">
          <div class="table-responsive">
            <table class="table table-sm rp-table mb-0">
              <thead>
                <tr>
                  <th style="width: 2.5rem">#</th>
                  <th>Item Name</th>
                  <th class="text-end">Unit Price</th>
                  <th class="text-end">Qty</th>
                  <th class="text-end">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="line in invoiceItems" :key="'i'+line.sl">
                  <td>{{ line.sl }}</td>
                  <td>{{ line.item_name || line.label || '—' }}</td>
                  <td class="text-end">{{ formatMoney(line.unit_price) }}</td>
                  <td class="text-end">{{ formatQty(line.qty) }}</td>
                  <td class="text-end fw-medium">{{ formatMoney(line.amount) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="audit-receipt-totals">
            <div v-for="(line, index) in invoiceTotals" :key="'t'+index" class="audit-receipt-total-row"
                 :class="{ 'is-emphasis': line.label === 'Total amount' }">
              <span>{{ line.label }}</span>
              <span>{{ line.value }}</span>
            </div>
          </div>
        </div>

        <!-- Differences: Old Data / New Data -->
        <div v-if="diffLines.length" class="table-responsive mb-3">
          <table class="table table-sm rp-table mb-0">
            <thead>
              <tr>
                <th>Field</th>
                <th>Old Data</th>
                <th>New Data</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(line, index) in diffLines" :key="'d'+index">
                <td class="fw-medium">{{ line.label || '—' }}</td>
                <td class="text-muted">{{ line.old || '—' }}</td>
                <td>{{ line.new || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Ledger posting lines (non-sale vouchers) -->
        <div v-if="!invoiceItems.length && lineDetails.length" class="table-responsive">
          <table class="table table-sm rp-table mb-0">
            <thead>
              <tr>
                <th>Account</th>
                <th class="text-end">Qty</th>
                <th class="text-end">Debit</th>
                <th class="text-end">Credit</th>
                <th>Narration</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(line, index) in lineDetails" :key="'l'+index">
                <td>{{ line.label || '—' }}</td>
                <td class="text-end">{{ line.quantity != null && Number(line.quantity) ? formatQty(line.quantity) : '—' }}</td>
                <td class="text-end">{{ line.debit != null && Number(line.debit) ? formatMoney(line.debit) : '—' }}</td>
                <td class="text-end">{{ line.credit != null && Number(line.credit) ? formatMoney(line.credit) : '—' }}</td>
                <td>{{ line.narration || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="!invoiceItems.length && !diffLines.length && !lineDetails.length" class="text-muted text-center py-4">
          No detail lines for this event.
        </div>

        <div class="audit-detail-foot">
          <button type="button" class="btn btn-primary" @click="closeDetails">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import { apiErrorMessage } from '@/helpers/apiError';
import inventoryReport from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import accountingReport from './accountingReportMixin';

const EMPTY_SUMMARY = {
  event_count: 0, create_count: 0, update_count: 0, actor_count: 0,
  action_counts: [], entity_counts: [],
};

const ACTION_TONES = {
  create: 'success',
  update: 'warning',
};

export default {
  name: 'AuditTrailReport',
  mixins: [inventoryReport, periodReport, accountingReport],

  reportUrl: apiUrls.audit_trail_report.get,
  fileName: 'audit_trail',
  columns: [],

  data() {
    return {
      filters: { ...defaultPeriod(), action: null, entity: null, actor: null, search: '' },
      summary: { ...EMPTY_SUMMARY },
      detailRow: null,
      totalRows: 0,
      // When true, the next fetchReport keeps the current page (page / pageSize changes).
      keepPageOnFetch: false,
    };
  },

  computed: {
    actionOptions() {
      return (this.options.actions || []).map((t) => ({ id: t.id, text: t.name }));
    },

    entityOptions() {
      return (this.options.entities || []).map((t) => ({ id: t.id, text: t.name }));
    },

    actorOptions() {
      return (this.options.actors || []).map((t) => ({ id: t.id, text: t.name }));
    },

    hasActiveFilters() {
      return this.periodChanged || !!(
        this.filters.action || this.filters.entity || this.filters.actor || this.filters.search.trim()
      );
    },

    /** Server already returns one page — do not re-slice client-side. */
    visibleRows() {
      return this.rows;
    },

    pageCount() {
      return Math.max(1, Math.ceil(this.totalRows / this.pageSize) || 1);
    },

    pageStart() {
      if (!this.totalRows) return 0;
      return (this.page - 1) * this.pageSize;
    },

    pageEnd() {
      if (!this.totalRows) return 0;
      return Math.min(this.pageStart + this.rows.length, this.totalRows);
    },

    pagedRows() {
      return this.rows;
    },

    detailLines() {
      return this.detailRow?.details || [];
    },

    invoiceTypeLine() {
      // Only show when the API sent an explicit Invoice type info row (sales omit it).
      const info = this.detailLines.find((d) => d.kind === 'info' && d.label === 'Invoice type');
      return info?.new || info?.value || '';
    },

    diffLines() {
      return this.detailLines.filter((d) => d.kind === 'diff');
    },

    lineDetails() {
      return this.detailLines.filter((d) => d.kind === 'line');
    },

    invoiceItems() {
      return this.detailLines.filter((d) => d.kind === 'invoice_item');
    },

    invoiceTotals() {
      return this.detailLines.filter((d) => d.kind === 'invoice_total');
    },
  },

  watch: {
    'filters.action'() { this.onFilterChange(); },
    'filters.entity'() { this.onFilterChange(); },
    'filters.actor'() { this.onFilterChange(); },
    pageSize(next, prev) {
      if (this.applyingFilters || next === prev) return;
      this.applyingFilters = true;
      this.page = 1;
      this.$nextTick(() => {
        this.applyingFilters = false;
        this.keepPageOnFetch = true;
        this.fetchReport();
      });
    },
  },

  methods: {
    actionTone(key) {
      return ACTION_TONES[key] || 'muted';
    },

    openDetails(row) {
      this.detailRow = row;
    },

    closeDetails() {
      this.detailRow = null;
    },

    goToPage(number) {
      if (number === '…') return;
      const next = Math.min(Math.max(1, number), this.pageCount);
      if (next === this.page) return;
      this.page = next;
      this.keepPageOnFetch = true;
      this.fetchReport();
    },

    onFilterChange() {
      if (this.applyingFilters) return;
      this.debouncedFetch.cancel();
      this.applyingFilters = true;
      this.page = 1;
      this.$nextTick(() => {
        this.applyingFilters = false;
        this.keepPageOnFetch = true;
        this.fetchReport();
      });
    },

    extraParams() {
      return {
        ...this.periodParams(),
        page: this.page,
        page_size: this.pageSize,
        ...(this.filters.action ? { action: this.filters.action } : {}),
        ...(this.filters.entity ? { entity: this.filters.entity } : {}),
        ...(this.filters.actor ? { actor: this.filters.actor } : {}),
      };
    },

    resetExtraFilters() {
      return { ...defaultPeriod(this.today), action: null, entity: null, actor: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
      const pagination = data?.pagination || {};
      this.totalRows = Number(pagination.total) || 0;
      if (pagination.page) this.page = Number(pagination.page) || this.page;
      this.detailRow = null;
    },

    async fetchReport() {
      const keepPage = this.keepPageOnFetch;
      this.keepPageOnFetch = false;
      const requestId = ++this.requestId;
      this.loading = true;
      this.error = '';
      if (!keepPage) this.page = 1;
      try {
        const { data } = await this.$axios.get(this.$options.reportUrl, { params: this.queryParams() });
        if (requestId !== this.requestId) return;
        this.options = data.options;
        this.storeId = data.filters?.store ?? null;
        this.rows = data.rows || [];
        this.expanded = {};
        this.applyResult(data);
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.error = apiErrorMessage(error);
        this.rows = [];
        this.totalRows = 0;
        this.applyResult(null);
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },

    printReport() {
      const s = this.summary;
      const search = this.filters.search.trim();
      this.printStatement(
        'Audit Trail',
        [
          this.periodText(),
          this.storeLabel(),
          this.filters.action && `Action: ${this.optionName(this.options.actions || [], this.filters.action)}`,
          this.filters.entity && `Entity: ${this.optionName(this.options.entities || [], this.filters.entity)}`,
          this.filters.actor && `User: ${this.optionName(this.options.actors || [], this.filters.actor)}`,
          search && `Search: ${search}`,
          `Page ${this.page} of ${this.pageCount}`,
        ],
        [
          ['Events', this.formatInt(s.event_count)],
          ['Creates', this.formatInt(s.create_count)],
          ['Updates', this.formatInt(s.update_count)],
          ['Users', this.formatInt(s.actor_count)],
        ],
        [
          { label: 'Date', value: (r) => this.formatDate(r.date) },
          { label: 'Time', value: (r) => r.time },
          { label: 'User', value: (r) => r.actor_name },
          { label: 'Action', value: (r) => r.action_label },
          { label: 'Type', value: (r) => r.type },
          { label: 'Voucher', value: (r) => r.voucher_id },
          { label: 'Store', value: (r) => r.store },
          { label: 'Summary', value: (r) => r.summary },
        ],
        this.rows,
        { Summary: `${s.event_count} events · ${s.actor_count} users` },
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>
<style scoped src="./accountingReport.css"></style>
<style scoped>
.audit-detail-btn {
  font-size: 0.8rem;
  padding: 0.2rem 0.65rem;
}

.audit-detail-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(9, 44, 76, 0.45);
}

.audit-detail-modal {
  width: min(860px, 100%);
  max-height: min(88vh, 820px);
  overflow: auto;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 16px 40px rgba(9, 44, 76, 0.18);
  padding: 1.25rem 1.35rem;
}

.audit-detail-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.audit-detail-meta {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  padding: 0.75rem;
  background: #FAFBFE;
  border: 1px solid #E6EAED;
  border-radius: 8px;
}

.audit-detail-meta span {
  display: block;
  font-size: 0.75rem;
  margin-bottom: 0.15rem;
}

.audit-detail-summary {
  color: #092C4C;
  font-size: 0.92rem;
}

.audit-invoice-type {
  padding: 0.65rem 0.85rem;
  background: rgba(254, 159, 67, 0.08);
  border: 1px solid rgba(254, 159, 67, 0.25);
  border-radius: 8px;
}

.audit-invoice-type span {
  font-size: 0.75rem;
}

.audit-receipt {
  border: 1px dashed #E6EAED;
  border-radius: 8px;
  overflow: hidden;
}

.audit-receipt-totals {
  border-top: 1px dashed #E6EAED;
  padding: 0.75rem 1rem;
  background: #FAFBFE;
}

.audit-receipt-total-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.2rem 0;
  font-size: 0.9rem;
  color: #092C4C;
}

.audit-receipt-total-row.is-emphasis {
  font-weight: 700;
  padding-top: 0.35rem;
  margin-top: 0.15rem;
  border-top: 1px solid #E6EAED;
}

.audit-detail-foot {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid #E6EAED;
}

@media (max-width: 767.98px) {
  .audit-detail-meta {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
